import type { StatsRow } from "../context/apiAccessStatsContext";

/**
 * APIアクセス統計 (#471 Phase E) の CSV エクスポート。
 * 出力するのは stats API が返す **集計済み series (bucket × status_class 単位)** であり、
 * 生ログ (api_access_logs の行) ではない点に注意 (生ログ CSV はバックエンド未実装 #457)。
 * apiKeyExport.ts と同方式 (Excel 互換 BOM + CRLF + RFC4180 クオート)。
 */

/** RFC4180 の最小 CSV セル化 (カンマ/改行/ダブルクオートを含む値はクオート + " を二重化)。 */
function csvCell(value: string): string {
  const s = value ?? "";
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export const STATS_CSV_HEADERS = [
  "bucket",
  "status_class",
  "call_count",
  "rate_limited_count",
  "avg_response_ms",
  "total_response_bytes",
] as const;

/** null は空セルにする (apiKeyExport の expires_at ?? "" と同方針)。 */
function numCell(value: number | null): string {
  return value == null ? "" : String(value);
}

/**
 * 取得済みの集計 series (StatsRow[]) を CSV 文字列にする。純関数 (テスト用)。
 * 1 StatsRow = 1 行 (bucket × status_class)。status_class は列として保持する (集計で潰さない)。
 */
export function buildStatsCsv(series: StatsRow[]): string {
  const header = STATS_CSV_HEADERS.join(",");
  const rows = series.map((r) =>
    [
      r.bucket,
      r.status_class,
      String(r.call_count),
      String(r.rate_limited_count),
      numCell(r.avg_response_ms),
      numCell(r.total_response_bytes),
    ]
      .map((v) => csvCell(v))
      .join(",")
  );
  return "﻿" + header + "\r\n" + (rows.length ? rows.join("\r\n") + "\r\n" : "");
}

/** ダウンロード用ファイル名 (key_prefix は ASCII 安全)。 */
export function statsCsvFilename(keyPrefix: string): string {
  return `aim-api-stats-${keyPrefix}.csv`;
}

/** ブラウザで集計 series の CSV をダウンロードする (Blob + 一時 <a>)。 */
export function downloadStatsCsv(series: StatsRow[], keyPrefix: string): void {
  const csv = buildStatsCsv(series);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = statsCsvFilename(keyPrefix);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
